import { createBrowserRouter, Navigate } from 'react-router-dom';
import { ProtectedRoute } from '@/components/protected-route';

// Layouts
import { AuthLayout } from '@/components/layouts/auth-layout';
import { SidebarLayout } from '@/components/layouts/sidebar-layout';

// Auth pages
import { SignInPage } from '@/pages/auth/signin';

// Dashboard and main pages
import HomePage from '@/pages/home';

// Review pages
import ReviewPage from '@/pages/review';
import ReviewDetailPage from '@/pages/review/org-detail';
import DeactivationDetailPage from '@/pages/review/deactivation-detail';

// Activation pages
import ActivationPage from '@/pages/activation';
import ActivationDetailPage from '@/pages/activation/task-detail';

// IT Tasks pages
import ITTasksPage from '@/pages/it-tasks';
import ITTaskDetailPage from '@/pages/it-tasks/task-detail';
import ITDeconfigTaskDetailPage from '@/pages/it-tasks/deconfig-task-detail';

// Data Tasks pages
import DataTasksPage from '@/pages/data-tasks';
import DataTaskDetailPage from '@/pages/data-tasks/task-detail';
import DataDeconfigTaskDetailPage from '@/pages/data-tasks/deconfig-task-detail';

// Users page
import UsersPage from '@/pages/users';

// Notifications page
import NotificationsPage from '@/pages/notifications';

// Status pages
import UnauthorizedPage from '@/pages/unauthorized';
import AccountInactivePage from '@/pages/account-inactive';
import PaymentSuccessPage from '@/pages/payment/success';

export const router = createBrowserRouter([
  // Redirect root to home
  {
    path: '/',
    element: <Navigate to="/home" replace />,
  },

  // Public auth routes
  {
    path: '/auth',
    element: <AuthLayout />,
    children: [
      {
        path: 'signin',
        element: <SignInPage />,
      },
    ],
  },

  // Protected routes with sidebar layout
  {
    element: (
      <ProtectedRoute>
        <SidebarLayout />
      </ProtectedRoute>
    ),
    children: [
      // Home/Dashboard (all authenticated users)
      {
        path: '/home',
        element: <HomePage />,
      },

      // Review routes (PLATFORM_TEAM and PLATFORM_ADMIN)
      {
        path: '/review',
        element: (
          <ProtectedRoute requiredRole={['PLATFORM_TEAM', 'PLATFORM_ADMIN']}>
            <ReviewPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/review/:orgId',
        element: (
          <ProtectedRoute requiredRole={['PLATFORM_TEAM', 'PLATFORM_ADMIN']}>
            <ReviewDetailPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/review/deactivation/:id',
        element: (
          <ProtectedRoute requiredRole={['PLATFORM_TEAM', 'PLATFORM_ADMIN']}>
            <DeactivationDetailPage />
          </ProtectedRoute>
        ),
      },

      // Activation routes (all authenticated users)
      {
        path: '/activation',
        element: <ActivationPage />,
      },
      {
        path: '/activation/:taskId',
        element: <ActivationDetailPage />,
      },

      // IT Tasks routes (IT_TEAM and IT_ADMIN)
      {
        path: '/it-tasks',
        element: (
          <ProtectedRoute requiredRole={['IT_TEAM', 'IT_ADMIN']}>
            <ITTasksPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/it-tasks/:taskId',
        element: (
          <ProtectedRoute requiredRole={['IT_TEAM', 'IT_ADMIN']}>
            <ITTaskDetailPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/it-tasks/deconfig/:taskId',
        element: (
          <ProtectedRoute requiredRole={['IT_TEAM', 'IT_ADMIN']}>
            <ITDeconfigTaskDetailPage />
          </ProtectedRoute>
        ),
      },

      // Data Tasks routes (DATA_TEAM and DATA_ADMIN)
      {
        path: '/data-tasks',
        element: (
          <ProtectedRoute requiredRole={['DATA_TEAM', 'DATA_ADMIN']}>
            <DataTasksPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/data-tasks/:taskId',
        element: (
          <ProtectedRoute requiredRole={['DATA_TEAM', 'DATA_ADMIN']}>
            <DataTaskDetailPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/data-tasks/deconfig/:taskId',
        element: (
          <ProtectedRoute requiredRole={['DATA_TEAM', 'DATA_ADMIN']}>
            <DataDeconfigTaskDetailPage />
          </ProtectedRoute>
        ),
      },

      // Users route (all admins)
      {
        path: '/users',
        element: (
          <ProtectedRoute requiredRole={['SYSTEM_ADMIN', 'IT_ADMIN', 'DATA_ADMIN', 'PLATFORM_ADMIN']}>
            <UsersPage />
          </ProtectedRoute>
        ),
      },

      // Notifications route (all authenticated users)
      {
        path: '/notifications',
        element: <NotificationsPage />,
      },
    ],
  },

  // Status pages (public access)
  {
    path: '/unauthorized',
    element: <UnauthorizedPage />,
  },
  {
    path: '/account-inactive',
    element: <AccountInactivePage />,
  },
  {
    path: '/payment/success',
    element: <PaymentSuccessPage />,
  },
]);
